import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowLeftRight, AlertTriangle, CheckCircle2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const longDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");

// Draw Trade: shows my conflicted performance, the alternates, and every rider who can take my spot.
export default function TradeFinderDialog({ rodeo, conflict, onClose, dark = false }) {
  const nav = useNavigate();
  const open = !!(rodeo && conflict);
  const [data, setData] = useState({});
  const [sent, setSent] = useState({});
  const [draw, setDraw] = useState(null);
  const events = conflict?.events || [];
  const evKey = events.join("|");

  useEffect(() => {
    if (!open) return;
    setDraw(null);
    api.get(`/rodeos/${rodeo.id}/draw`).then((r) => setDraw(r.data)).catch(() => setDraw({ events: [], performances: [] }));
  }, [open, rodeo?.id]);

  useEffect(() => {
    if (!open) return;
    setData({}); setSent({});
    evKey.split("|").filter(Boolean).forEach((ev) => {
      api.get(`/rodeos/${rodeo.id}/trade-candidates`, { params: { event: ev, my_performance_id: conflict.performance_id } })
        .then((r) => setData((d) => ({ ...d, [ev]: r.data })))
        .catch(() => setData((d) => ({ ...d, [ev]: { candidates: [], alternates: [] } })));
    });
  }, [open, rodeo?.id, conflict?.performance_id, evKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = async (ev, cand) => {
    const k = `${ev}|${cand.contestant}`;
    setSent((s) => ({ ...s, [k]: "sending" }));
    try {
      const { data: r } = await api.post("/trades/request", { rodeo_id: rodeo.id, event: ev, my_performance_id: conflict.performance_id, candidate_name: cand.contestant });
      setSent((s) => ({ ...s, [k]: "sent" }));
      toast.success(r.duplicate ? "You already asked this rider" : r.notified ? `${cand.contestant} was sent your trade request` : `Saved — ${cand.contestant} isn't on Rodeo Pilot yet`);
    } catch (e) {
      setSent((s) => ({ ...s, [k]: null }));
      toast.error(apiError(e.response?.data?.detail) || "Could not send trade request");
    }
  };

  const c = dark
    ? { dlg: "bg-[#161B22] text-slate-100 border border-white/10", sub: "text-slate-400", box: "bg-white/5 border-white/10", row: "border-white/10", warn: "bg-[#3A1A1D] border-[#5C2328] text-[#F5A3AA]", name: "text-slate-100", div: "divide-white/10" }
    : { dlg: "bg-white text-[#14181F] border border-[#E4E7EB]", sub: "text-slate-500", box: "bg-[#F7F8FA] border-[#E4E7EB]", row: "border-[#EEF0F2]", warn: "bg-[#FDECEE] border-[#F5C2C7] text-[#9E0008]", name: "text-[#14181F]", div: "divide-[#EEF0F2]" };
  const alts = Object.values(data)[0]?.alternates || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${c.dlg} w-[calc(100vw-1rem)] max-w-2xl max-h-[88vh] overflow-y-auto`} data-testid="trade-finder-dialog">
        {open && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Trade · {rodeo.name}</DialogTitle>
              <DialogDescription className={c.sub}>Find a rider who can take your spot so your rodeos don't clash.</DialogDescription>
            </DialogHeader>
            <div className={`rounded-lg border px-3 py-2.5 text-sm flex items-start gap-2 ${conflict.conflicts_with?.length ? c.warn : c.box}`} data-testid="trade-conflict-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                You're up in <b>{conflict.performance_label}</b> on <b>{longDate(conflict.date)}</b>
                {conflict.conflicts_with?.length ? <> — same day as <b>{conflict.conflicts_with.map((o) => `${o.rodeo_name} (${o.performance_label})`).join(", ")}</b></> : " — no clash with your other rodeos right now"}.
              </div>
            </div>
            <div className="text-xs" data-testid="trade-alternates">
              <div className={`font-semibold mb-1.5 ${c.sub}`}>Performances & slack you could move to</div>
              <div className="flex flex-wrap gap-1.5">
                {alts.length ? alts.map((a) => (
                  <span key={a.id} className={`rounded-md border px-2 py-1 ${c.box}`} data-testid={`trade-alt-${a.id}`}>
                    <b className={`text-[9px] mr-1 ${a.type === "slack" ? "text-purple-500" : "text-blue-500"}`}>{a.type === "slack" ? "SLACK" : "PERF"}</b>{a.label} · {fmtDate(a.date)}
                  </span>
                )) : <span className={c.sub}>Loading…</span>}
              </div>
            </div>
            {events.length === 0 && <div className={`text-sm py-6 text-center ${c.sub}`}>Add your events in your profile so we know which event to trade.</div>}
            {events.map((ev) => (
              <div key={ev} className="space-y-2">
                <EventCandidates ev={ev} d={data[ev]} sent={sent} onRequest={request} c={c} />
                <OriginalDraw ev={ev} draw={draw} myPerf={conflict.performance_id} c={c} />
              </div>
            ))}
            <button onClick={() => { onClose(); nav("/trades"); }} data-testid="trade-finder-my-requests"
              className="w-full mt-1 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold py-2.5 rounded-lg">My trade requests</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OriginalDraw({ ev, draw, myPerf, c }) {
  const [open, setOpen] = useState(false);
  const block = (draw?.events || []).find((e) => e.event === ev);
  if (!block) return null;
  const perfs = (draw.performances || []).filter((p) => block.rows.some((r) => r.performance_id === p.id));
  const slug = ev.replace(/\s/g, "-");
  return (
    <div className={`rounded-lg border ${c.row}`} data-testid={`trade-original-draw-${slug}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`trade-original-draw-toggle-${slug}`}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold">
        <span>Original draw · {block.total} entered</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5">
          {perfs.map((p) => {
            const rows = block.rows.filter((r) => r.performance_id === p.id);
            return (
              <div key={p.id}>
                <div className={`text-[11px] font-bold uppercase tracking-wide mb-1 ${p.id === myPerf ? "text-[#C8000F]" : c.sub}`}>
                  {p.label} · {fmtDate(p.date)} · {rows.length} up{p.id === myPerf ? " · your performance" : ""}
                </div>
                <div className={`text-[12px] ${c.name} grid sm:grid-cols-2 gap-x-4`}>
                  {rows.map((r, i) => <div key={i} className="truncate">{r.position}. {block.team ? `${r.header} / ${r.heeler}` : r.contestant}</div>)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventCandidates({ ev, d, sent, onRequest, c }) {
  const slug = ev.replace(/\s/g, "-");
  if (!d) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>;
  const cands = d.candidates || [];
  return (
    <div data-testid={`trade-event-${slug}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-display font-bold uppercase text-sm tracking-wide">{ev}</div>
        <div className={`text-xs ${c.sub}`} data-testid={`trade-count-${slug}`}>{cands.length} can make the trade</div>
      </div>
      {cands.length === 0 ? (
        <div className={`text-sm py-4 text-center rounded-lg border border-dashed ${c.row} ${c.sub}`}>No one entered here is free on your day yet.</div>
      ) : (
        <div className={`rounded-lg border ${c.row} divide-y ${c.div}`}>
          {cands.map((a, i) => {
            const st = sent[`${ev}|${a.contestant}`];
            return (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`trade-cand-${slug}-${i}`}>
                <div className="min-w-0">
                  <div className={`text-sm font-semibold truncate ${c.name}`}>{a.contestant}</div>
                  <div className={`text-[11px] ${c.sub}`}>Up {a.current_draw} · {fmtDate(a.current_draw_date)}{a.hometown ? ` · ${a.hometown}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.has_account && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#3B6B1E]/15 text-[#5FA83B]">ON RODEO PILOT</span>}
                  {st === "sent" ? (
                    <span className="text-xs font-semibold text-[#16A34A] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Requested</span>
                  ) : (
                    <button onClick={() => onRequest(ev, a)} disabled={st === "sending"} data-testid={`trade-request-${slug}-${i}`}
                      className="text-xs font-bold rounded-full px-3 py-1 bg-[#C8000F] text-white hover:bg-[#9E0008] disabled:opacity-60 inline-flex items-center gap-1">
                      {st === "sending" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowLeftRight className="w-3 h-3" />} Request
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className={`text-[11px] mt-1.5 ${c.sub}`}>Riders on Rodeo Pilot get your request in the app. Phone and email are shared once they accept, if they've opted in.</p>
    </div>
  );
}
